import React, { useState } from 'react';
import { usePreventGameKeydownListening } from '@hooks';
import {
  Box,
  Modal,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';

import { IBranding, IBrandingListModalProps } from './interfaces';
import { useStyles } from './styles';

/**
 * Renders the configuration library modal: a searchable card grid where a
 * card click - or its Activate button - makes that configuration live, and its
 * Edit button opens the edit flow. Cloning and deleting live in the card's
 * three-dots menu, which repeats Edit for discoverability.
 *
 * @param props.open - Whether the modal is shown.
 * @param props.onClose - Called when the modal is dismissed via its close button.
 * @param props.onLeaveBrandingMode - Called by the footer button to exit editor mode.
 * @param props.onCreateNew - Called by the header button to start the create flow.
 * @param props.brandings - Configurations of the current map to list.
 * @param props.brandingsLoading - Shows the loading state instead of the grid.
 * @param props.onToggleBranding - Called with a configuration id to activate it.
 * @param props.onEditBranding - Called with a configuration id to open the edit flow.
 * @param props.onCloneBranding - Called with a configuration id to clone it.
 * @param props.onDeleteBranding - Called with a configuration id to delete it.
 * @param props.liveBrandingId - Id the room actually has live; falls back to `isActive` when null.
 * @param props.eventName - Experience name shown in the subtitle.
 */
const BrandingListModal = ({
  open,
  onClose,
  onLeaveBrandingMode,
  onCreateNew,
  brandings,
  brandingsLoading = false,
  onToggleBranding,
  onEditBranding,
  onCloneBranding,
  onDeleteBranding,
  liveBrandingId,
  eventName,
}: IBrandingListModalProps) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = usePreventGameKeydownListening<HTMLInputElement>();
  const [menuState, setMenuState] = useState<{
    anchorEl: HTMLElement;
    brandingId: string;
    isActive: boolean;
  } | null>(null);

  // The room's live configuration, never the one the editor has open: inside
  // the editor those differ, and marking the edited one "Active" told the
  // author it was on air when it was not.
  const getIsCurrent = (branding: IBranding): boolean =>
    liveBrandingId != null ? branding.id === liveBrandingId : branding.isActive;

  const visibleBrandings = brandings
    .filter((b) => b.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      const aIsCurrent = getIsCurrent(a);
      const bIsCurrent = getIsCurrent(b);

      if (aIsCurrent && !bIsCurrent) {
        return -1;
      }
      if (!aIsCurrent && bIsCurrent) {
        return 1;
      }
      return 0;
    });

  const getRelativeTime = (timestamp?: number): string => {
    if (!timestamp) {
      return '';
    }
    const diffDays = Math.floor((Date.now() - timestamp) / 86400000);
    if (diffDays === 0) {
      return translate('adminToolbox.editedToday', 'edited today');
    }
    if (diffDays === 1) {
      return translate('adminToolbox.editedYesterday', 'edited yesterday');
    }
    return translate(
      'adminToolbox.editedDaysAgo',
      'edited {{count}} days ago',
      {
        count: diffDays,
      },
    );
  };

  const renderPalette = (branding: IBranding) => {
    const { brandingColors } = branding;
    const hasPrimary = Boolean(brandingColors?.primaryColor);
    const hasSecondary = Boolean(brandingColors?.secondaryColor);
    const hasOutline = Boolean(brandingColors?.outlineColor);

    if (hasPrimary || hasSecondary || hasOutline) {
      return (
        <>
          <Box
            sx={{
              ...classes.colorStripe,
              backgroundColor:
                brandingColors?.primaryColor || 'rgba(255,255,255,0.1)',
            }}
          />
          <Box
            sx={{
              ...classes.colorStripe,
              backgroundColor:
                brandingColors?.secondaryColor || 'rgba(255,255,255,0.1)',
            }}
          />
          <Box
            sx={{
              ...classes.colorStripe,
              backgroundColor:
                brandingColors?.outlineColor || 'rgba(255,255,255,0.1)',
            }}
          />
        </>
      );
    }
    return <Box sx={classes.palettePlaceholder} />;
  };

  /**
   * Empties the search box and puts the caret back in it, so the next query
   * can be typed without reaching for the mouse again.
   */
  const handleClearSearch = () => {
    setSearchQuery('');
    searchInputRef.current?.focus();
  };

  /**
   * Activates the clicked configuration; a click on the already-active card
   * is a no-op.
   *
   * @param branding - Configuration whose card was clicked.
   */
  const handleCardClick = (branding: IBranding) => {
    if (getIsCurrent(branding)) {
      return;
    }
    onToggleBranding(branding.id);
  };

  // The whole card activates on click, so every control inside it has to stop
  // the click from bubbling up to the card.
  const handleEdit = (e: React.MouseEvent<HTMLElement>, id: string) => {
    e.stopPropagation();
    onEditBranding?.(id);
  };

  const handleActivate = (e: React.MouseEvent<HTMLElement>, id: string) => {
    e.stopPropagation();
    onToggleBranding(id);
  };

  const handleMenuOpen = (
    e: React.MouseEvent<HTMLElement>,
    brandingId: string,
    isActive: boolean,
  ) => {
    e.stopPropagation();
    setMenuState({ anchorEl: e.currentTarget, brandingId, isActive });
  };

  const handleMenuClose = () => {
    setMenuState(null);
  };

  /**
   * Opens the edit flow for the configuration whose three-dots menu is open -
   * the same flow the card's own Edit button opens.
   */
  const handleEditFromMenu = () => {
    if (menuState) {
      onEditBranding?.(menuState.brandingId);
    }
    handleMenuClose();
  };

  const handleClone = () => {
    if (menuState) {
      onCloneBranding?.(menuState.brandingId);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (menuState) {
      onDeleteBranding?.(menuState.brandingId);
    }
    handleMenuClose();
  };

  const subtitleParts = eventName
    ? [eventName, String(brandings.length)]
    : [String(brandings.length)];
  const subtitle = subtitleParts.join(' · ');

  return (
    <Modal
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      sx={classes.modalRoot}
    >
      <Box sx={classes.container}>
        <Box sx={classes.header}>
          <Box sx={classes.headerLeft}>
            <Typography sx={classes.title}>
              {translate(
                'adminToolbox.availableBrandings',
                'Available configurations',
              )}
            </Typography>
            <Typography sx={classes.subtitle}>{subtitle}</Typography>
          </Box>
          <Box sx={classes.headerRight}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={onCreateNew}
              sx={classes.createHeaderButton}
              data-testid="branding-list-create-new-btn"
            >
              {translate(
                'adminToolbox.createNewBranding',
                'Create new configuration',
              )}
            </Button>
            <IconButton
              onClick={onClose}
              sx={classes.closeButton}
              size="small"
              aria-label="close"
              data-testid="branding-list-close-btn"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box sx={classes.searchWrapper}>
          <TextField
            inputRef={searchInputRef}
            fullWidth
            size="small"
            placeholder={translate(
              'adminToolbox.searchBrandings',
              'Search configurations',
            )}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            inputProps={{ 'data-testid': 'branding-list-search-input' }}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
            }}
            onKeyUp={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={classes.searchIcon} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    sx={classes.searchClearButton}
                    aria-label={translate(
                      'adminToolbox.clearSearch',
                      'Clear search',
                    )}
                    data-testid="branding-list-search-clear-btn"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={classes.searchField}
          />
        </Box>

        <Box sx={classes.content}>
          {brandingsLoading ? (
            <Box sx={classes.stateBox}>
              <CircularProgress size={24} />
              <Typography sx={classes.stateText}>
                {translate(
                  'adminToolbox.loadingBrandings',
                  'Loading configurations...',
                )}
              </Typography>
            </Box>
          ) : visibleBrandings.length === 0 ? (
            <Box sx={classes.stateBox}>
              <Typography sx={classes.stateText}>
                {translate(
                  'adminToolbox.noBrandingsYet',
                  'No configurations yet. Create your first one.',
                )}
              </Typography>
            </Box>
          ) : (
            <Box sx={classes.cardGrid}>
              {visibleBrandings.map((branding) => {
                const isCurrent = getIsCurrent(branding);
                const relTime = getRelativeTime(branding.timestamp);

                return (
                  <Tooltip
                    key={branding.id}
                    title={
                      isCurrent
                        ? translate('editorMode.liveTooltip', 'Currently live')
                        : translate(
                            'editorMode.activateOnSelect',
                            'Click to make this configuration live',
                          )
                    }
                    placement="top"
                    enterDelay={400}
                  >
                    <Box
                      sx={[classes.card, isCurrent && classes.cardActive]}
                      onClick={() => handleCardClick(branding)}
                      data-testid={`branding-list-row-${branding.id}`}
                    >
                      <Box sx={classes.cardPalette}>
                        {renderPalette(branding)}
                        {isCurrent && (
                          <Box sx={classes.liveBadge}>
                            <Box sx={classes.liveDot} />
                            <Typography sx={classes.liveBadgeText}>
                              {translate('adminToolbox.onAir', 'Active')}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <Box sx={classes.cardBody}>
                        <Typography sx={classes.cardName}>
                          {branding.name === 'Unnamed'
                            ? translate('adminToolbox.unnamed', 'Unnamed')
                            : branding.name}
                        </Typography>
                        {relTime && (
                          <Typography sx={classes.cardMeta}>
                            {relTime}
                          </Typography>
                        )}

                        <Box sx={classes.cardFooterRow}>
                          {!isCurrent && (
                            <Button
                              size="small"
                              startIcon={<PowerSettingsNewIcon />}
                              onClick={(e) => handleActivate(e, branding.id)}
                              sx={classes.activateButton}
                              data-testid={`branding-list-row-${branding.id}-activate-btn`}
                            >
                              {translate('adminToolbox.activate', 'Activate')}
                            </Button>
                          )}
                          <Button
                            size="small"
                            startIcon={<EditOutlinedIcon />}
                            onClick={(e) => handleEdit(e, branding.id)}
                            sx={classes.editButton}
                            data-testid={`branding-list-row-${branding.id}-edit-btn`}
                          >
                            {translate('adminToolbox.edit', 'Edit')}
                          </Button>
                          <IconButton
                            size="small"
                            onClick={(e) =>
                              handleMenuOpen(e, branding.id, isCurrent)
                            }
                            sx={classes.moreButton}
                            aria-label="more"
                            data-testid={`branding-list-row-${branding.id}-select-btn`}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          )}
        </Box>

        <Box sx={classes.footer}>
          <Button
            variant="outlined"
            onClick={onLeaveBrandingMode}
            sx={classes.leaveButton}
            data-testid="branding-list-leave-btn"
          >
            {translate('adminToolbox.leaveEditorMode', 'Leave editor mode')}
          </Button>
        </Box>

        <Menu
          anchorEl={menuState?.anchorEl}
          open={Boolean(menuState)}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          PaperProps={{ sx: classes.menuPaper }}
        >
          <MenuItem
            onClick={handleEditFromMenu}
            sx={classes.menuItem}
            // Distinct from the card's own inline `-edit-btn`: both render for
            // the same configuration while this menu is open, and sharing one
            // id makes the pair ambiguous to a test even within a single row.
            data-testid={
              menuState
                ? `branding-list-row-${menuState.brandingId}-edit-menu-btn`
                : undefined
            }
          >
            <EditOutlinedIcon sx={classes.menuItemIcon} />
            {translate('adminToolbox.edit', 'Edit')}
          </MenuItem>
          <MenuItem
            onClick={handleClone}
            sx={classes.menuItem}
            data-testid={
              menuState
                ? `branding-list-row-${menuState.brandingId}-clone-btn`
                : undefined
            }
          >
            <ContentCopyOutlinedIcon sx={classes.menuItemIcon} />
            {translate('common.copy', 'Copy')}
          </MenuItem>
          <MenuItem
            onClick={handleDelete}
            disabled={menuState?.isActive ?? false}
            sx={classes.menuItem}
            data-testid={
              menuState
                ? `branding-list-row-${menuState.brandingId}-delete-btn`
                : undefined
            }
          >
            <DeleteOutlineIcon sx={classes.menuItemIcon} />
            {translate('common.delete', 'Delete')}
          </MenuItem>
        </Menu>
      </Box>
    </Modal>
  );
};

export default BrandingListModal;
